import {
  LightningOutRenderer,
  LIGHTNING_OUT_APPLICATION,
  CreateComponentStatus,
} from '../types';
import StandardInjector from '../Injector/StandardInjector';

/**
 * A LightningOutRenderer for the standard Lightning.out flow
 *
 * @remarks
 * Typically, when the Lightning.out script is injected into the dom,
 * the client should have access to the $Lightning object on the top frame.
 * This renderer should be used in most cases.
 */
export default class StandardRenderer implements LightningOutRenderer {
  onInitializationError?: (error: Error) => void;

  onComponentError?: (msg: string) => void;

  constructor(
    onInitializationError?: (e: Error) => void,
    onComponentError?: (msg: string) => void,
  ) {
    this.onInitializationError = onInitializationError;
    this.onComponentError = onComponentError;
  }

  async render(
    container: HTMLElement,
    lightningComponents: string[],
    recordId: string,
    lightningUrl: string,
    accessToken: string,
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      container.innerHTML = '';
      if (!StandardInjector.isInjected(window.document)) {
        try {
          await StandardInjector.inject(window.document, lightningUrl);
        } catch (e) {
          reject(e);
        }
      }

      const onInitialized = (): void => {
        Promise.all(
          lightningComponents.map(componentName =>
            this.renderComponent(container, componentName, recordId),
          ),
        )
          .then(() => resolve())
          .catch(reason => {
            if (this.onComponentError) {
              this.onComponentError(reason);
              resolve();
            } else {
              reject(reason);
            }
          });
      };

      try {
        window.$Lightning.use(
          LIGHTNING_OUT_APPLICATION,
          onInitialized,
          lightningUrl,
          accessToken,
        );
      } catch (e) {
        console.log('lightning.use failed');
        if (this.onInitializationError) {
          this.onInitializationError(e);
          resolve();
        } else {
          reject(e);
        }
      }
    });
  }

  private async renderComponent(
    parentEl: HTMLElement,
    componentName: string,
    recordId: string,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const element = document.createElement('div');
      parentEl.appendChild(element);

      const onCreated = (
        _cmp: any, // eslint-disable-line @typescript-eslint/no-explicit-any
        _status: CreateComponentStatus,
        errorMessage: string,
      ): void => {
        if (errorMessage) {
          reject(new Error(`${componentName} failed because ${errorMessage}`));
        } else {
          resolve();
        }
      };

      window.$Lightning.createComponent(
        componentName,
        {
          recordId,
        },
        element,
        onCreated,
      );
    });
  }
}
